/**
 * How a workspace tab paints.
 *
 * Its own module because `TabBar.tsx` may export only components
 * (`react-refresh/only-export-components`), and because the painting rules are
 * worth reading — and testing — in one place, away from the drag-and-drop
 * machinery that surrounds them.
 *
 * The model is the browser window's. The strip is the FRAME: the rail's own
 * surface, so rail and strip are one L of chrome around the page. The active
 * tab is a pill made of the page's colour, floating inside that frame. Nothing
 * is drawn — no closing rule, no borders, no accent — because a pill is bounded
 * by its fill against the frame and needs none of them.
 *
 * Two earlier treatments failed for reasons worth keeping in view. The first
 * strip was the page's own lightness, so five tabs read as five words. The fix
 * (`docs/plans/2026-09-11-tab-strip-legibility-design.md`) gave the strip a
 * ground and painted the active tab `--card` — but Inbox, Tasks and every page
 * without a root colour paint `--background`, so on the surfaces used daily the
 * active tab was the brightest object on screen, with a stripe on top and
 * hairlines down both sides: a drawn box, not part of the page. See
 * `docs/plans/2026-09-16-tab-strip-pill-design.md`.
 */
import { destinationById } from '@shared/navigation/destinations';

import type { WorkspaceTab } from '../types';
import type { DomainKey } from '@shared/components/ui';

/**
 * Tab height: 26px, the rail row (28px) turned sideways. With the strip's 4px
 * above and below, the strip is 34px — four fewer than the folder tab this
 * replaces, and the density at which a 13px label still holds its own beside
 * a page title. 30px was tried and cramps the close control.
 */
export const TAB_HEIGHT = 'h-6.5';

/**
 * Geometry every tab shares, active or not: a pill, rounded on all four
 * corners, with no borders.
 *
 * Nothing may hang BELOW the strip's padding box. The strip is
 * `overflow-x-auto`, and CSS computes the other axis to `auto` whenever one
 * axis is not `visible` — so the strip scrolls vertically too, and one stray
 * pixel is enough to raise a scrollbar down its right edge.
 */
export const TAB_SHELL =
  `group relative ${TAB_HEIGHT} flex items-center gap-1.5 rounded-md ` +
  'text-[13px] font-medium whitespace-nowrap transition-colors';

/**
 * How the strip itself paints: the rail's surface, pills centred with 4px of
 * inset and 2px between them. The structural classes — `hidden nav:flex`,
 * `overflow-x-auto`, the chat-toggle clearance — stay with the component.
 *
 * There is deliberately no closing rule. The frame simply ends where the page
 * begins; a line there would draw the very edge the pill exists to avoid.
 */
export const STRIP_PAINT = 'bg-sidebar items-center gap-0.5 px-1.5 py-1';

/**
 * The coloured glyph a tab shows, or undefined for none.
 *
 * The same duotone icon and `--domain-*` token the rail row uses, so a tab is
 * findable by shape and colour instead of by reading its title — and so the
 * strip's icons and the rail's, now on the same surface, read as one system.
 * A pinned tab keeps the pin instead: an 80px tab cannot carry two glyphs, and
 * the pin is the only visible marker that a tab is pinned. A path outside the
 * catalog has no domain; the gap between pills bounds it.
 */
export function tabDomain(tab: WorkspaceTab): DomainKey | undefined {
  if (tab.pinned) return undefined;
  return destinationById(tab.destinationId ?? '')?.domain;
}

/**
 * How one tab paints, given its state.
 *
 * Active: the page's colour, so the pill reads as "the one that is open"
 * without having to touch the page. In the unfocused pane of a split it fades
 * toward the frame — focus is shown by subtraction, never by an accent.
 * Inactive: the rail row's text colour, and hover is the rail row's hover tint
 * in the same pill shape, so hovering previews exactly what activating does.
 */
export function tabClasses(opts: {
  pinned: boolean;
  isActive: boolean;
  isFocused: boolean;
}): string {
  const { pinned, isActive, isFocused } = opts;
  const padding = pinned ? 'px-2' : 'px-2.5';
  const active = isFocused
    ? 'bg-background text-foreground'
    : 'bg-background/55 text-foreground/75';
  // NOT hover:bg-sidebar — the strip IS bg-sidebar, so that fill would be invisible.
  const inactive = 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent';
  return `${TAB_SHELL} ${padding} ${isActive ? active : inactive}`;
}

/**
 * The count beside an inbox tab's title: a digit-wide pill in the tab's own
 * colours. A badge, not "(3)" in the label — a count is a quantity beside a
 * name, not part of it, and the badge is what makes the tab narrow.
 */
export function badgeClasses(isActive: boolean): string {
  const tone = isActive
    ? 'bg-foreground/10 text-foreground'
    : 'bg-sidebar-foreground/10 text-sidebar-foreground/80';
  return `rounded-full px-1.5 py-px text-[10px] leading-none font-semibold tabular-nums ${tone}`;
}
